import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { renderGoogleButton, handleGoogleRedirectCallback } from '../utils/googleAuth';
import { BrandFormShell, FormSection } from '../components/BrandFormShell';
import SEOHead from '../components/SEOHead';

const inputClass = 'w-full px-5 py-4 rounded-2xl border border-[#d9e5ef] bg-white focus:outline-none focus:ring-4 focus:ring-[#294c79]/10 focus:border-[#294c79] transition text-base';

export default function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const googleBtnRef = useRef(null);

  useEffect(() => {
    const result = handleGoogleRedirectCallback();
    if (result) {
      axios.post(`${process.env.REACT_APP_API_URL}/api/auth/google`, { token: result.credential })
        .then(res => {
          if (res.data.status === 'success') {
            login(res.data.user, res.data.token);
            setTimeout(() => navigate(result.returnPath || '/dashboard', { replace: true }), 100);
          } else {
            setError(res.data.message || 'Google Sign-In failed.');
          }
        })
        .catch(() => setError('Google Sign-In failed. Please try again.'));
      return;
    }

    if (googleBtnRef.current) {
      renderGoogleButton(
        process.env.REACT_APP_GOOGLE_CLIENT_ID,
        googleBtnRef.current,
        async (credential) => {
          setError('');
          try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/google`, { token: credential });
            if (res.data.status === 'success') {
              login(res.data.user, res.data.token);
              setTimeout(() => {
                const from = location.state?.from?.pathname || location.state?.from || '/dashboard';
                navigate(from, { replace: true });
              }, 100);
            } else {
              setError(res.data.message || 'Google Sign-In failed.');
            }
          } catch (err) {
            setError(err.response?.data?.error || 'Google Sign-In failed. Please try again.');
          }
        },
        (errMsg) => { if (errMsg) setError(errMsg); }
      );
    }
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/login`, form);
      if (res.data.status === 'success') {
        login(res.data.user || { email: form.email }, res.data.token);
        const from = location.state?.from || '/dashboard';
        navigate(from, { replace: true });
      } else {
        setError(res.data.message || 'Login failed');
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.response?.data?.message || '';
      if (msg.toLowerCase().includes('google')) {
        setError(msg);
      } else if (status === 401) {
        setError('Incorrect email or password. Please try again.');
      } else if (status === 404) {
        setError('No account found with this email. Please sign up.');
      } else if (status === 403) {
        setError('Your account is not verified yet. Please complete verification.');
      } else if (status === 429) {
        setError('Too many login attempts. Please wait a few minutes and try again.');
      } else {
        setError(msg || 'Unable to sign in. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead title="Login | LiteRent" description="Sign in to your LiteRent dashboard to manage your profile, applications, payments, and notifications." canonical="https://literent.com.ng/login" />
      <div className="min-h-screen bg-gradient-to-br from-[#f8fbff] via-white to-[#f5f9ec] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-5 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-2xl">
            <BrandFormShell
              showLogo
              centerHero
              badge="Tenant sign in"
              title="Welcome back to LiteRent"
              subtitle="Pick up right where you left off. Your dashboard keeps track of your profile, house applications, payment stages, and every LiteRent update."
              footer={<p>New to LiteRent? <Link to="/signup" className="text-[#294c79] font-semibold underline">Create your account</Link>.</p>}
            >
              {error ? (
                <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>
              ) : null}

              <FormSection title="Choose how you want to sign in" description="Use Google for speed or continue with your LiteRent email and password.">
                <div ref={googleBtnRef} className="w-full mb-6 flex justify-center" style={{ minHeight: '44px' }} />
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex-1 h-px bg-[#e5e7eb]" />
                  <span className="text-sm text-[#1a1a1a]/45 font-medium">or continue with email</span>
                  <div className="flex-1 h-px bg-[#e5e7eb]" />
                </div>

                <form onSubmit={handleLogin} className="space-y-5">
                  <label className="block">
                    <span className="block text-sm font-semibold text-[#294c79] mb-2">Email Address</span>
                    <input id="email" name="email" type="email" autoComplete="email" required value={form.email} onChange={handleChange} placeholder="you@example.com" className={inputClass} />
                  </label>

                  <label className="block">
                    <span className="block text-sm font-semibold text-[#294c79] mb-2">Password</span>
                    <div className="relative">
                      <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required value={form.password} onChange={handleChange} placeholder="••••••••" className={`${inputClass} pr-16`} />
                      <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#294c79] hover:text-[#1f3a5c] font-medium">
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </label>

                  <div className="flex items-center justify-between gap-3 text-sm">
                    <p className="text-[#1a1a1a]/55">Secure access to your LiteRent dashboard.</p>
                    <Link to="/forgot-password" className="text-[#294c79] hover:text-[#1f3a5c] font-semibold hover:underline">
                      Forgot password?
                    </Link>
                  </div>

                  <button type="submit" disabled={loading} className={`w-full py-4 px-6 rounded-2xl text-white font-semibold text-lg transition-all shadow-md ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#294c79] hover:bg-[#1f3a5c] active:bg-[#172f4c]'}`}>
                    {loading ? 'Signing you in…' : 'Sign In'}
                  </button>
                </form>
              </FormSection>
            </BrandFormShell>
          </div>
        </div>

        <footer className="py-6 text-center text-[#1a1a1a]/60 text-sm border-t border-[#afc09e]/30">
          © {new Date().getFullYear()} LiteRent. All rights reserved.
        </footer>
      </div>
    </>
  );
}
